/* eslint-disable no-console, no-plusplus, prefer-arrow-callback, no-await-in-loop */
import html2canvas from 'html2canvas';
import JsPDF from 'jspdf';
import { getPdfDom } from 'common/dom/getPdfDom';
import { SELECTED_HEIGHT, SELECTED_WIDTH } from './constants';
import { CONVERSION_RATIOS, FILE_SETTINGS, IMAGE_SETTINGS, PdfExportOptions, PdfExportResult } from './types';
import { Logger } from '../../../utilities';

export interface MultiplePagesExportOptions extends PdfExportOptions {
  urls: string[];
  msDelayBetweenPages?: number;
  onProgress?: (currentPage: number, totalPages: number, percentage?: number) => void;
}

export interface MultiplePagesExportResult extends PdfExportResult {
  totalPages?: number;
  processedPages?: number;
}

// Network request timeout constants
const RECENT_REQUEST_THRESHOLD_MS = 5000; // 5 seconds

// Timeout constants for various operations
const TIMEOUT_MS = {
  IFRAME_LOAD: 15000, // 15 seconds - timeout for iframe to load
  API_CHECK_RETRY: 500, // 500ms - retry interval for checking API calls
  DOWNLOAD_DELAY: 500, // 500ms - delay to ensure download has started
  DOWNLOAD_MAX_WAIT: 2000, // 2 seconds - maximum wait time for download to initiate
} as const;

// Progress percentage constants
const PROGRESS_PERCENTAGE = {
  MAX: 100, // Maximum percentage (100%)
  CAP: 99, // Cap percentage to show until final completion
  CONVERSION_FACTOR: 100, // Factor to convert 0.0-1.0 to 0-100 percentage
} as const;

// Progress milestones for page processing (0.0 to 1.0 per page)
const PROGRESS_MILESTONES = {
  INITIAL: 0.02,
  AFTER_IFRAME_LOAD: 0.15,
  BEFORE_API_WAIT: 0.20,
  AFTER_API_COMPLETE: 0.40,
  CAPTURING_HEADER: 0.50,
  CAPTURING_CONTENT: 0.70,
  CAPTURING_FOOTER: 0.85,
  PAGE_COMPLETE: 1.0,
} as const;

/**
 * Check if there are any pending network requests using Performance API
 */
const hasPendingNetworkRequests = (): boolean => {
  try {
    // Use Performance API to check for pending requests
    const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];

    // Check if any resource has responseEnd === 0 (indicating pending request)
    const hasPendingResources = resources.some(resource => resource.responseEnd === 0);

    if (hasPendingResources) {
      Logger.info('Found pending network requests via Performance API');
      return true;
    }

    // Additional check: look for recent requests that might still be processing
    const recentRequests = resources.filter(resource => {
      const now = performance.now();
      const requestTime = resource.startTime;
      const duration = now - requestTime;

      // If request started less than 5 seconds ago and responseEnd is 0, it might still be pending
      return duration < RECENT_REQUEST_THRESHOLD_MS && resource.responseEnd === 0;
    });

    if (recentRequests.length > 0) {
      Logger.info(`Found ${recentRequests.length} recent requests that might still be pending`);
      return true;
    }

    return false;
  } catch (error) {
    Logger.warn('Error checking pending requests via Performance API:', error);
    return false;
  }
};

/**
 * Monitor fetch and XMLHttpRequest calls to track pending requests
 */
const setupNetworkMonitoring = (): { hasPendingRequests: () => boolean; cleanup: () => void } => {
  let pendingFetchRequests = 0;
  let pendingXHRRequests = 0;

  // Monitor fetch requests
  const originalFetch = window.fetch;
  window.fetch = (...args) => {
    pendingFetchRequests++;
    Logger.info(`Fetch request started (${pendingFetchRequests} pending)`);

    return originalFetch.apply(this, args).finally(() => {
      pendingFetchRequests--;
      Logger.info(`Fetch request completed (${pendingFetchRequests} pending)`);
    });
  };

  // Monitor XMLHttpRequest
  const originalXHROpen = XMLHttpRequest.prototype.open;
  const originalXHRSend = XMLHttpRequest.prototype.send;

  // eslint-disable-next-line func-names
  XMLHttpRequest.prototype.open = function (
    method: string,
    url: string | URL,
    async: boolean = true,
    username?: string | null,
    password?: string | null
  ) {
    originalXHROpen.call(this, method, url, async, username, password);
    this.addEventListener(
      'loadend',
      () => {
        pendingXHRRequests--;
        Logger.info(`XHR request completed (${pendingXHRRequests} pending)`);
      },
      { once: true }
    );
  };

  // eslint-disable-next-line func-names
  XMLHttpRequest.prototype.send = function (body?: Document | XMLHttpRequestBodyInit | null) {
    pendingXHRRequests++;
    Logger.info(`XHR request started (${pendingXHRRequests} pending)`);
    originalXHRSend.call(this, body);
  };

  const hasPendingRequests = () => pendingFetchRequests > 0 || pendingXHRRequests > 0;

  const cleanup = () => {
    window.fetch = originalFetch;
    XMLHttpRequest.prototype.open = originalXHROpen;
    XMLHttpRequest.prototype.send = originalXHRSend;
  };

  return { hasPendingRequests, cleanup };
};

/**
 * Wait for all API calls to complete and pdfContentRef element to be present and loaded
 */
const waitForApiCallsAndPdfContentElement = async (document: Document, maxWaitTime: number = 30000): Promise<void> =>
  new Promise<void>(resolve => {
    const startTime = Date.now();

    // Setup network monitoring
    const { hasPendingRequests: hasPendingMonitoredRequests, cleanup } = setupNetworkMonitoring();

    const checkForApiCallsAndElement = () => {
      // Check for pending network requests using multiple methods
      const hasPendingViaPerformance = hasPendingNetworkRequests();
      const hasPendingViaMonitoring = hasPendingMonitoredRequests();

      if (hasPendingViaPerformance || hasPendingViaMonitoring) {
        Logger.info('Network requests still pending, waiting...');
        setTimeout(checkForApiCallsAndElement, TIMEOUT_MS.API_CHECK_RETRY);
        return;
      }

      // Look for element with pdfContentRef attribute
      const pdfContentElement = document.querySelector('[data-ref="pdfContentRef"]');

      if (pdfContentElement) {
        Logger.info('Found pdfContentRef element, checking if content is loaded...');

        // Check if element has content (not just skeleton)
        const hasContent = pdfContentElement.children.length > 0;

        // Check for skeleton elements that might still be visible
        const skeletonElements = document.querySelectorAll(
          '[data-testid*="skeleton"], [data-testid*="loading"], ' +
            '.skeleton, .loading-skeleton, .shimmer, ' +
            '[class*="skeleton"], [class*="loading"], ' +
            '[aria-label*="loading"], [aria-label*="skeleton"]'
        );

        const hasVisibleSkeletons = Array.from(skeletonElements).some(element => {
          const style = window.getComputedStyle(element);
          return (
            style.display !== 'none' &&
            style.visibility !== 'hidden' &&
            style.opacity !== '0' &&
            (element as HTMLElement).offsetWidth > 0 &&
            (element as HTMLElement).offsetHeight > 0
          );
        });

        if (hasContent && !hasVisibleSkeletons) {
          Logger.info('pdfContentRef element is loaded with content and no skeletons visible');
          Logger.info('Waiting 4 seconds for content to fully settle...');

          // Wait 4 seconds for content to fully settle after API calls are done
          setTimeout(() => {
            Logger.info('4-second delay completed, proceeding with PDF generation');
            cleanup(); // Clean up monitoring
            resolve();
          }, 4000);
          return;
        }

        if (hasVisibleSkeletons) {
          Logger.info('Skeleton elements still visible, waiting...');
        }
      }

      // Check if we've exceeded max wait time
      if (Date.now() - startTime > maxWaitTime) {
        Logger.warn('Timeout waiting for API calls and pdfContentRef element, proceeding anyway');
        cleanup(); // Clean up monitoring
        resolve();
        return;
      }

      // Check again in 500ms
      setTimeout(checkForApiCallsAndElement, 500);
    };

    checkForApiCallsAndElement();
  });

/**
 * Export multiple pages as a single PDF by navigating through URLs
 */
export const exportWithIframes = async (options: MultiplePagesExportOptions): Promise<MultiplePagesExportResult> => {
  try {
    Logger.info('exportWithIframes called with options:', options);
    const { urls, onProgress, ...pdfOptions } = options;

    if (!urls || urls.length === 0) {
      Logger.error('No URLs provided for multiple pages export', new Error('No URLs provided for multiple pages export'));
      return { success: false, error: 'No URLs provided for multiple pages export' };
    }

    Logger.info(`Starting export of ${urls.length} pages`);

    // Store canvas data for each page to create robust final PDF
    const pageCanvasData: {
      headerCanvas: HTMLCanvasElement | null;
      contentCanvas: HTMLCanvasElement | null;
      footerCanvas: HTMLCanvasElement | null;
      scale: number;
      pageWidthMm: number;
      pageHeightMm: number;
    }[] = [];

    for (let i = 0; i < urls.length; i++) {
      const url = urls[i];
      const currentPage = i + 1;
      const totalPages = urls.length;
      
      // Add page parameters to URL
      const urlWithPageParams = new URL(url);
      urlWithPageParams.searchParams.set('currentPage', currentPage.toString());
      urlWithPageParams.searchParams.set('totalPages', totalPages.toString());
      const finalUrl = urlWithPageParams.toString();
      
      Logger.info(`Processing URL ${currentPage}/${totalPages}: ${finalUrl}`);

      // Calculate progress base for this page (each page contributes 1/totalPages of total progress)
      const pageProgressBase = ((currentPage - 1) / totalPages) * PROGRESS_PERCENTAGE.CONVERSION_FACTOR;
      
      // Helper function to report progress with gradual increments
      const reportProgress = (progressWithinPage: number) => {
        const totalProgress = pageProgressBase + (progressWithinPage / totalPages) * PROGRESS_PERCENTAGE.CONVERSION_FACTOR;
        onProgress?.(currentPage, totalPages, Math.min(totalProgress, PROGRESS_PERCENTAGE.CAP)); // Cap at 99% until final
      };
      
      // Report initial progress when starting to process a page
      reportProgress(PROGRESS_MILESTONES.INITIAL);

      // Create a hidden iframe to load the page so the user doesn't see the navigation
      const iframe = document.createElement('iframe');
      iframe.src = finalUrl;
      iframe.style.position = 'fixed';
      iframe.style.zIndex = '9999';                  
      iframe.style.left = '-9999px';
      // Virtual big screen size to render all content properly and not be cropped
      iframe.style.width = '2560px';
      iframe.style.height = '1440px';

      Logger.info('Created iframe, adding to DOM');
      document.body.appendChild(iframe);

      try {
        // Wait for iframe to load
        Logger.info('Waiting for iframe to load...');
        await new Promise<void>((resolve, reject) => {
          const timeout = setTimeout(() => {
            Logger.error(`Timeout loading ${url}`, new Error(`Timeout loading ${url}`));
            reject(new Error(`Timeout loading ${url}`));
          }, TIMEOUT_MS.IFRAME_LOAD);

          iframe.onload = () => {
            Logger.info(`Iframe loaded for ${url}`);
            clearTimeout(timeout);
            reportProgress(PROGRESS_MILESTONES.AFTER_IFRAME_LOAD); // Report progress after iframe loads
            resolve();
          };

          iframe.onerror = () => {
            Logger.error(`Failed to load ${url}`, new Error(`Failed to load ${url}`));
            clearTimeout(timeout);
            reject(new Error(`Failed to load ${url}`));
          };
        });

        // Get iframe document reference
        const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
        if (!iframeDoc) {
          throw new Error(`Could not access iframe content for ${url}`);
        }

        // Wait for API calls to complete and pdfContentRef element to be present and loaded
        Logger.info('Waiting for API calls to complete and pdfContentRef element to load...');
        reportProgress(PROGRESS_MILESTONES.BEFORE_API_WAIT); // Report progress before waiting for API calls
        await waitForApiCallsAndPdfContentElement(iframeDoc);
        reportProgress(PROGRESS_MILESTONES.AFTER_API_COMPLETE); // Report progress after API calls complete

        // Find the elements to export
        const { header: pdfHeaderElement, content: pdfContentElement, footer: pdfFooterElement } = getPdfDom(iframeDoc);

        // Capture elements separately for proper positioning
        let headerCanvas: HTMLCanvasElement | null = null;
        let contentCanvas: HTMLCanvasElement | null = null;
        let footerCanvas: HTMLCanvasElement | null = null;

        // Capture header
        reportProgress(PROGRESS_MILESTONES.CAPTURING_HEADER);
        if (pdfHeaderElement) {
          headerCanvas = await html2canvas(pdfHeaderElement as HTMLElement, {
            scale: 2,            
            useCORS: true,
            allowTaint: true,
            width: (pdfHeaderElement as HTMLElement).scrollWidth,
            height: (pdfHeaderElement as HTMLElement).scrollHeight,
            logging: false,
          });
        }

        // Capture content
        reportProgress(PROGRESS_MILESTONES.CAPTURING_CONTENT);
        if (pdfContentElement) {
          contentCanvas = await html2canvas(pdfContentElement as HTMLElement, {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            width: (pdfContentElement as HTMLElement).scrollWidth,
            height: (pdfContentElement as HTMLElement).scrollHeight,
            logging: false,
          });
        }

        // Capture footer
        reportProgress(PROGRESS_MILESTONES.CAPTURING_FOOTER);
        if (pdfFooterElement) {
          footerCanvas = await html2canvas(pdfFooterElement as HTMLElement, {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            width: (pdfFooterElement as HTMLElement).scrollWidth,
            height: (pdfFooterElement as HTMLElement).scrollHeight,
            logging: false,
          });
        }

        // Calculate total content dimensions to determine orientation
        const headerHeight = headerCanvas ? headerCanvas.height : 0;
        const contentHeight = contentCanvas ? contentCanvas.height : 0;
        const footerHeight = footerCanvas ? footerCanvas.height : 0;
        const totalContentHeight = headerHeight + contentHeight + footerHeight;
        
        const maxElementWidth = Math.max(
          headerCanvas ? headerCanvas.width : 0,
          contentCanvas ? contentCanvas.width : 0,
          footerCanvas ? footerCanvas.width : 0
        );

        // Use fixed portrait orientation for all pages
        const pageWidthMm = SELECTED_WIDTH;
        const pageHeightMm = SELECTED_HEIGHT;
        
        Logger.info(`Page ${i + 1} orientation: Portrait (${pageWidthMm}x${pageHeightMm}mm)`);

        // Calculate scaling to fit content on Letter page
        const pageWidthPx = pageWidthMm * CONVERSION_RATIOS.MM_TO_PX;
        const pageHeightPx = pageHeightMm * CONVERSION_RATIOS.MM_TO_PX;
        
        // Calculate scale factor to fit content while maintaining readability
        const widthScale = pageWidthPx / maxElementWidth;
        const heightScale = pageHeightPx / totalContentHeight;
        const scale = Math.min(widthScale, heightScale, 2); // Cap at 2x for performance
        
        Logger.info(`Page ${i + 1} scale factor: ${scale.toFixed(2)}`);

        // Store element data for later PDF creation
        pageCanvasData.push({
          headerCanvas,
          contentCanvas,
          footerCanvas,
          scale,
          pageWidthMm,
          pageHeightMm,
        });

        Logger.info(
          `Successfully captured page ${i + 1} (header + content + footer) with dimensions ${pageWidthMm.toFixed(2)}x${pageHeightMm.toFixed(2)}mm`
        );

        // Report progress when page is completed
        reportProgress(PROGRESS_MILESTONES.PAGE_COMPLETE); // Mark page as 100% complete (within its page slot)
      } catch (error) {
        Logger.error(`Error processing URL ${url}`, error);
        // Skip this URL and continue with the next one
        // eslint-disable-next-line no-continue
        continue;
      } finally {
        // Clean up iframe
        Logger.info('Cleaning up iframe...');
        try {
          document.body.removeChild(iframe);
        } catch (cleanupError) {
          Logger.warn('Error cleaning up iframe:', cleanupError);
        }
      }
    }

    if (pageCanvasData.length === 0) {
      Logger.error('No canvas data was captured', new Error('No canvas data was captured'));
      return { success: false, error: 'No canvas data was captured' };
    }

    // Create final PDF from canvas data with proper element positioning
    Logger.info(`Creating final PDF from ${pageCanvasData.length} pages...`);
    
    // Initialize PDF with first page dimensions
    const firstPage = pageCanvasData[0];
    const finalPdf = new JsPDF({
      unit: FILE_SETTINGS.PDF_UNIT,
      format: [firstPage.pageWidthMm, firstPage.pageHeightMm],
      compress: false,
    });
    
    for (let i = 0; i < pageCanvasData.length; i++) {
      const { headerCanvas, contentCanvas, footerCanvas, scale, pageWidthMm, pageHeightMm } = pageCanvasData[i];
      
      if (i > 0) {
        // Add new page with this page's dimensions
        finalPdf.addPage([pageWidthMm, pageHeightMm]);
      }
      
      let currentY = 0;
      
      // Add header at the top
      if (headerCanvas) {
        const headerImgData = headerCanvas.toDataURL('image/png', IMAGE_SETTINGS.MAX_QUALITY);
        const scaledHeaderWidth = (headerCanvas.width * scale) / CONVERSION_RATIOS.MM_TO_PX;
        const scaledHeaderHeight = (headerCanvas.height * scale) / CONVERSION_RATIOS.MM_TO_PX;
        
        // Center header horizontally
        const headerX = (pageWidthMm - scaledHeaderWidth) / 2;
        
        finalPdf.addImage(headerImgData, IMAGE_SETTINGS.PNG_FORMAT, headerX, currentY, scaledHeaderWidth, scaledHeaderHeight);
        currentY += scaledHeaderHeight;
        
        Logger.info(`Added header to page ${i + 1} at position (${headerX.toFixed(2)}, ${currentY.toFixed(2)})`);
      }
      
      // Add content below header
      if (contentCanvas) {
        const contentImgData = contentCanvas.toDataURL('image/png', IMAGE_SETTINGS.MAX_QUALITY);
        const scaledContentWidth = (contentCanvas.width * scale) / CONVERSION_RATIOS.MM_TO_PX;
        const scaledContentHeight = (contentCanvas.height * scale) / CONVERSION_RATIOS.MM_TO_PX;
        
        // Center content horizontally
        const contentX = (pageWidthMm - scaledContentWidth) / 2;
        
        finalPdf.addImage(contentImgData, IMAGE_SETTINGS.PNG_FORMAT, contentX, currentY, scaledContentWidth, scaledContentHeight);
        currentY += scaledContentHeight;
        
        Logger.info(`Added content to page ${i + 1} at position (${contentX.toFixed(2)}, ${currentY.toFixed(2)})`);
      }
      
      // Add footer at the bottom
      if (footerCanvas) {
        const footerImgData = footerCanvas.toDataURL('image/png', IMAGE_SETTINGS.MAX_QUALITY);
        const scaledFooterWidth = (footerCanvas.width * scale) / CONVERSION_RATIOS.MM_TO_PX;
        const scaledFooterHeight = (footerCanvas.height * scale) / CONVERSION_RATIOS.MM_TO_PX;
        
        // Center footer horizontally
        const footerX = (pageWidthMm - scaledFooterWidth) / 2;
        
        // Position footer at the bottom of the page
        const footerY = pageHeightMm - scaledFooterHeight;
        
        finalPdf.addImage(footerImgData, IMAGE_SETTINGS.PNG_FORMAT, footerX, footerY, scaledFooterWidth, scaledFooterHeight);
        
        Logger.info(`Added footer to page ${i + 1} at position (${footerX.toFixed(2)}, ${footerY.toFixed(2)})`);
      }
      
      Logger.info(`Completed page ${i + 1} (Portrait) with dimensions ${pageWidthMm.toFixed(2)}x${pageHeightMm.toFixed(2)}mm`);
    }

    // Download the final PDF
    const filename = pdfOptions.filename || 'multiple-pages-export';
    Logger.info(`Downloading final PDF: ${filename}.pdf`);
    
    try {
      finalPdf.save(`${filename}.pdf`);
      Logger.info('PDF save() method called successfully');
    } catch (saveError) {
      Logger.error('Error calling finalPdf.save():', saveError);
      // Continue anyway - the PDF was created, even if save failed
      // The error might be due to browser restrictions or popup blockers
    }

    // Wait for download to initiate with timeout protection
    // Note: We cannot reliably detect if a browser download actually started
    // due to browser security restrictions, but we can handle errors from save()
    // If the download doesn't start (e.g., blocked by browser), we still report success
    // because the PDF was generated successfully - the download failure is a browser limitation
    await new Promise<void>(resolve => {
      const startTime = Date.now();
      
      setTimeout(() => {
        const elapsed = Date.now() - startTime;
        
        // If we waited longer than the maximum expected time, log a warning
        if (elapsed > TIMEOUT_MS.DOWNLOAD_MAX_WAIT) {
          Logger.warn(
            `Wait time (${elapsed}ms) exceeded maximum (${TIMEOUT_MS.DOWNLOAD_MAX_WAIT}ms). ` +
            'Download may have been blocked by browser or popup blocker, but PDF was generated successfully.'
          );
        } else {
          Logger.info('PDF download should have initiated');
        }
        
        // Report completion - the PDF was generated successfully
        // Note: Even if download was blocked, the PDF creation succeeded
        onProgress?.(urls.length, urls.length, PROGRESS_PERCENTAGE.MAX);
        resolve();
      }, TIMEOUT_MS.DOWNLOAD_DELAY);
    });

    Logger.info('Multiple pages export completed successfully');
    return {
      success: true,
      filename: `${filename}.pdf`,
      totalPages: urls.length,
      processedPages: pageCanvasData.length,
    };
  } catch (error) {
    Logger.error('Error in exportMultiplePagesAsPdf:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred during multiple pages export';
    return {
      success: false,
      error: errorMessage,
    };
  }
};
